import { memo, useCallback, useMemo, useState } from "react";

import { LangCode } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent } from "@/components/ui/dialog/dialog";
import { CheckboxField } from "@/components/ui/form/fields/check-box-field/check-box";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";

import { useHandleFormState } from "./hooks";

type Props = {
	email: string;

	onUpdateAndResendGuestEmail: (guestEmail: string, langCode?: LangCode) => Promise<void>;
};

export const UpdateAndResendGuestEmailOfReservationDetailDialog = memo(
	({ email, onUpdateAndResendGuestEmail }: Props) => {
		const {
			guestEmail,
			isSendEnOfI18n,
			guestEmailErrorMessage,
			hasError,
			handleChangeGuestEmail,
			handleChangeIsSendEnOfI18n,
		} = useHandleFormState(email);

		const [canOpenDialog, setCanOpenDialog] = useState(false);
		/**
		 * ダイアログを表示
		 */
		const handleClickOpenDialog = useCallback(() => {
			setCanOpenDialog(true);
		}, []);
		/**
		 * ダイアログの表示・非表示を制御
		 */
		const onChangeCanOpenDialog = useCallback((open: boolean) => {
			setCanOpenDialog(open);
		}, []);

		/**
		 * 更新 & 送信
		 */
		const handleClickUpdateAndResendGuestEmail = useCallback(() => {
			const langCode = isSendEnOfI18n ? LangCode.En : LangCode.Default;

			onUpdateAndResendGuestEmail(guestEmail, langCode).then(() => onChangeCanOpenDialog(false));
		}, [guestEmail, isSendEnOfI18n, onChangeCanOpenDialog, onUpdateAndResendGuestEmail]);

		const disabled = useMemo(() => hasError, [hasError]);

		/**
		 * ダイアログを表示するボタン
		 */
		if (!canOpenDialog) {
			return (
				<Button variant="outline" type="button" size="sm" onClick={handleClickOpenDialog}>
					変更/再送
				</Button>
			);
		}

		return (
			<Dialog open={canOpenDialog} onOpenChange={onChangeCanOpenDialog}>
				<DialogContent>
					<div className="p-2">
						<p>予約詳細のお客様のメールアドレスが更新され、予約情報がお客様に再送信されます。</p>

						<div className="mt-4">
							<div className="space-y-5">
								<InputTextField
									value={guestEmail}
									errorMessage={guestEmailErrorMessage}
									handleChange={handleChangeGuestEmail}
								/>

								<div className="flex justify-center">
									<CheckboxField
										value={isSendEnOfI18n}
										label="確認メールを英語で再送する"
										handleChange={handleChangeIsSendEnOfI18n}
									/>
								</div>
							</div>
						</div>

						<div className="mt-6 text-right">
							<Button type="button" disabled={disabled} onClick={handleClickUpdateAndResendGuestEmail}>
								送信する
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		);
	},
);
